// src/hooks/useRequestActions.js
import { useCallback } from "react";
import { supabase } from "../lib/supabaseClient";
import { useToast } from "../context/ToastContext";

export function useRequestActions(me, setRequests, setSelectedIds) {
  const { setToast } = useToast();

  // ---------------- RPC CALLS ----------------
  const callRpc = async (fn, args) => {
    const { data, error } = await supabase.rpc(fn, args);
    if (error) throw error;
    return data;
  };

  // ---------------- REQUEST ACTIONS ----------------
  const onMarkTrash = useCallback(async (row) => {
    try {
      await callRpc("foia_mark_trash", { p_request: row.id });
      setRequests((prev) =>
        prev.map((r) => (r.id === row.id ? { ...r, status: "trash" } : r))
      );
      setToast({ type: "ok", msg: "Marked as trash." });
      setSelectedIds(prev => {
        const next = new Set(prev);
        next.delete(row.id);
        return next;
      });
    } catch (e) {
      setToast({ type: "error", msg: e.message });
    }
  }, [setRequests, setSelectedIds, setToast]);

  const onMarkDenied = useCallback(async (row) => {
    try {
      await callRpc("foia_mark_denied", { p_request: row.id });
      setRequests((prev) =>
        prev.map((r) => (r.id === row.id ? { ...r, status: "denied" } : r))
      );
      setToast({ type: "ok", msg: "Marked denied." });
    } catch (e) {
      setToast({ type: "error", msg: e.message });
    }
  }, [setRequests, setToast]);

  const onMarkReceived = useCallback(async (row) => {
    try {
      await callRpc("foia_mark_received", { p_request: row.id });
      setRequests((prev) =>
        prev.map((r) => (r.id === row.id ? { ...r, status: "received" } : r))
      );
      setToast({ type: "ok", msg: "Marked received." });
    } catch (e) {
      setToast({ type: "error", msg: e.message });
    }
  }, [setRequests, setToast]);

  const onMarkDelayed = useCallback(async (row) => {
    try {
      await callRpc("foia_mark_delayed", { p_request: row.id });
      setRequests((prev) =>
        prev.map((r) => (r.id === row.id ? { ...r, status: "delayed" } : r))
      );
      setToast({ type: "ok", msg: "Marked delayed." });
    } catch (e) {
      setToast({ type: "error", msg: e.message });
    }
  }, [setRequests, setToast]);

  const onMarkNoBodycam = useCallback(async (row) => {
    if (!me?.org_id) {
      setToast({ type: "warn", msg: "No organization context yet." });
      return;
    }
    try {
      const { error } = await supabase
        .from("foia_requests")
        .update({ status: "no_bodycam" })
        .eq("id", row.id)
        .eq("org_id", me.org_id);
      if (error) throw error;
      setRequests((prev) =>
        prev.map((r) => (r.id === row.id ? { ...r, status: "no_bodycam" } : r))
      );
      setToast({ type: "ok", msg: "Marked as no bodycam." });
    } catch (e) {
      setToast({ type: "error", msg: e.message });
    }
  }, [me?.org_id, setRequests, setToast]);

  const onMarkRequested = useCallback(async (row) => {
    if (!me?.org_id) {
      setToast({ type: "warn", msg: "No organization context yet." });
      return;
    }
    try {
      // Allow optionally passing a case_number on the row object
      const payload = { status: "requested", date_requested: new Date().toISOString() };
      if (row && row.case_number && String(row.case_number).trim()) {
        payload.case_number = String(row.case_number).trim();
      }

      const { error } = await supabase
        .from("foia_requests")
        .update(payload)
        .eq("id", row.id)
        .eq("org_id", me.org_id);
      if (error) throw error;
      setRequests((prev) =>
        prev.map((r) =>
          r.id === row.id ? { ...r, status: "requested", date_requested: new Date().toISOString(), ...(payload.case_number ? { case_number: payload.case_number } : {}) } : r
        )
      );
      setToast({ type: "ok", msg: "Marked as requested." });
      setSelectedIds(prev => {
        const next = new Set(prev);
        next.delete(row.id);
        return next;
      });
    } catch (e) {
      setToast({ type: "error", msg: e.message });
    }
  }, [me?.org_id, setRequests, setSelectedIds, setToast]);

  // Invoice flow: accept an amount parameter provided by the UI layer.
  // This removes reliance on the browser prompt and allows callers to present a styled modal.
  const onInvoice = useCallback(async (row, amount) => {
    if (!me?.org_id || !me?.id) {
      setToast({ type: "warn", msg: "No user/org context yet." });
      return;
    }
    // amount must be provided by the caller (UI). If missing, warn and exit.
    if (amount == null) {
      setToast({ type: "warn", msg: "No invoice amount provided." });
      return;
    }
    const num = Number(amount);
    if (!Number.isFinite(num) || num <= 0) {
      setToast({ type: "warn", msg: "Invalid amount." });
      return;
    }
    try {
      const { error: invErr } = await supabase.from("invoices").insert({
        org_id: me.org_id,
        foia_request_id: row.id,
        amount: num,
        created_by: me.id,
      });
      if (invErr) throw invErr;
      const { error: updErr } = await supabase
        .from("foia_requests")
        .update({ status: "fee_pending", fee: num })
        .eq("id", row.id);
      if (updErr) throw updErr;

      setRequests((prev) =>
        prev.map((r) =>
          r.id === row.id ? { ...r, status: "fee_pending", fee: num } : r
        )
      );
      setToast({ type: "ok", msg: "Invoice recorded, status set to fee_pending." });
    } catch (e) {
      setToast({ type: "error", msg: e.message });
    }
  }, [me?.org_id, me?.id, setRequests, setToast]);

  const onEditRequest = useCallback(async (updatedRow) => {
    try {
      // Create patch object with only the fields that might have changed
      const patch = {
        location: updatedRow.location,
        agency: updatedRow.agency,
        incident_date: updatedRow.incident_date,
        status: updatedRow.status,
        name_suspect: updatedRow.name_suspect,
        notes: updatedRow.notes,
        article_url: updatedRow.article_url,
        email_for_request: updatedRow.email_for_request,
        how_to_request: updatedRow.how_to_request,
        draft_subject: updatedRow.draft_subject,
        draft_email: updatedRow.draft_email,
        fee: updatedRow.fee,
        footage_link: updatedRow.footage_link,
        date_requested: updatedRow.date_requested,
        last_reply_date: updatedRow.last_reply_date,
      };
      // Prefer the server-side RPC if available (centralized business logic),
      // but fall back to a direct table update if the RPC is missing or
      // references stale columns (e.g. "column ... does not exist").
      let rpcError = null;
      try {
        const { error } = await supabase.rpc("foia_update_request", {
          p_request: updatedRow.id,
          p_patch: patch,
        });
        if (error) throw error;
      } catch (e) {
        // Keep reference for later handling
        rpcError = e;
      }

      // If RPC failed due to undefined column on the server, fallback to direct update
      if (rpcError) {
        const message = String(rpcError.message || rpcError.error || rpcError);
        const isUndefinedColumn = /column .* does not exist/i.test(message) || (rpcError.code === '42703');
        if (isUndefinedColumn) {
          // Attempt direct table update as a safe fallback
          const { error: updErr } = await supabase
            .from('foia_requests')
            .update(patch)
            .eq('id', updatedRow.id);
          if (updErr) throw updErr;
          // Inform user that we used a fallback because the RPC was stale
          setToast({ type: 'warn', msg: 'Server-side update RPC failed; applied direct update as fallback.' });
        } else {
          // If it's not an undefined-column error, rethrow to be handled below
          throw rpcError;
        }
      }
      
      setRequests((prev) =>
        prev.map((r) => (r.id === updatedRow.id ? { ...r, ...patch } : r))
      );
      setToast({ type: "ok", msg: "Request updated successfully." });
    } catch (e) {
      setToast({ type: "error", msg: e.message });
    }
  }, [setRequests, setToast]);

  const onDeleteRequest = useCallback(async (row) => {
    if (!me?.org_id) {
      setToast({ type: "warn", msg: "No organization context yet." });
      return;
    }
    try {
      const { error } = await supabase
        .from("foia_requests")
        .delete()
        .eq("id", row.id)
        .eq("org_id", me.org_id);
      
      if (error) throw error;
      
      setRequests((prev) => prev.filter((r) => r.id !== row.id));
      setSelectedIds(prev => {
        const next = new Set(prev);
        next.delete(row.id);
        return next;
      });
      setToast({ type: "ok", msg: "Request permanently deleted." });
    } catch (e) {
      setToast({ type: "error", msg: e.message });
    }
  }, [me?.org_id, setRequests, setSelectedIds, setToast]);

  // ---------------- BULK ACTIONS ----------------
  const onBulkMarkRequested = useCallback(async (selectedIds) => {
    if (!me?.org_id) {
      setToast({ type: "warn", msg: "No organization context yet." });
      return;
    }
    if (selectedIds.size === 0) {
      setToast({ type: "warn", msg: "No items selected." });
      return;
    }
    
    const ids = Array.from(selectedIds);
    const confirmed = window.confirm(`Mark ${ids.length} request(s) as "requested"?`);
    if (!confirmed) return;

    try {
      const { error } = await supabase
        .from("foia_requests")
        .update({ status: "requested", date_requested: new Date().toISOString() })
        .in("id", ids)
  .eq("org_id", me.org_id);
      
      if (error) throw error;

      setRequests((prev) =>
        prev.map((r) =>
          ids.includes(r.id) ? { ...r, status: "requested", date_requested: new Date().toISOString() } : r
        )
      );
      setSelectedIds(new Set());
      setToast({ type: "ok", msg: `${ids.length} request(s) marked as requested.` });
    } catch (e) {
      setToast({ type: "error", msg: e.message });
    }
  }, [me?.org_id, setRequests, setSelectedIds, setToast]);

  const onBulkTrash = useCallback(async (selectedIds) => {
    if (selectedIds.size === 0) {
      setToast({ type: "warn", msg: "No items selected." });
      return;
    }
    
    const ids = Array.from(selectedIds);
    const confirmed = window.confirm(`Move ${ids.length} request(s) to trash?`);
    if (!confirmed) return;

    try {
      for (const id of ids) {
        await callRpc("foia_mark_trash", { p_request: id });
      }

      setRequests((prev) =>
        prev.map((r) =>
          ids.includes(r.id) ? { ...r, status: "trash" } : r
        )
      );
      setSelectedIds(new Set());
      setToast({ type: "ok", msg: `${ids.length} request(s) moved to trash.` });
    } catch (e) {
      setToast({ type: "error", msg: e.message });
    }
  }, [setRequests, setSelectedIds, setToast]);

  return {
    onMarkTrash,
    onMarkDenied,
    onMarkReceived,
    onMarkDelayed,
    onMarkNoBodycam,
    onMarkRequested,
    onInvoice,
    onEditRequest,
    onDeleteRequest,
    onBulkMarkRequested,
    onBulkTrash,
  };
}
